import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import {
  ContractFlowCustomFieldFragment,
  ContractFlowCustomFieldValueFragment,
  ContractVersionsPageDocument,
  ContractsContractPoliciesFragment,
  ContractsCustomFieldsPoliciesFragment,
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesEmployeeFragment,
  ContractsEmployeesJobCatalogContextlessPoliciesFragment,
  ContractsEmployeesReferenceContractFragment,
  EmployeeProfilePageDocument,
  JobCatalogTreeFragment,
  JobPositionLevelDocument,
  JobPositionRoleDocument,
  UpdateContractsContractVersionFlowDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { ValidationError } from 'modules/core/lib/errors'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import type { FlashType } from 'modules/core/store/models/app'
import app from 'modules/core/store/models/app'

import DatesAndDetails from 'modules/contracts/components/FlowPage/Steps/DatesAndDetails'
import LegalAndExtras from 'modules/contracts/components/FlowPage/Steps/LegalAndExtras'
import RoleLevelSalary from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary'
import WorkHours from 'modules/contracts/components/FlowPage/Steps/WorkHours'
import { FlowPageContent } from 'modules/contracts/components/FlowPage/_FlowPageContent'
import ContractFlow from 'modules/contracts/lib/ContractFlow'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getEditedCustomFields } from 'modules/contracts/lib/customFields'
import { isGraphQLType } from 'modules/contracts/lib/types'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'

import Footer from './_Footer'
import DatesStep from './lib/DatesStep'
import LegalAndExtrasStep from './lib/LegalAndExtrasStep'
import RoleLevelSalaryStep from './lib/RoleLevelSalaryStep'
import WorkHoursStep from './lib/WorkHoursStep'
import { translations as datesStepTranslations } from './lib/datesTranslations'
import { translations as legalAndExtrasTranslations } from './lib/legalAndExtrasTranslations'
import { translations as roleLevelSalaryTranslations } from './lib/roleLevelSalaryTranslations'
import { translations as templateTranslations } from './lib/templateTranslations'
import { getUpdateFlowForm } from './lib/updateFlowForm'

interface Props {
  customFields: ContractFlowCustomFieldFragment[]
  customValues: ContractFlowCustomFieldValueFragment[]
  contractVersion: ContractsEmployeesReferenceContractFragment | null
  contractVersions: ContractsEmployeesContractVersionFragment[]
  employee: ContractsEmployeesEmployeeFragment
  contractContextlessPolicies: ContractsContractPoliciesFragment
  jobCatalogContextlessPolicies: ContractsEmployeesJobCatalogContextlessPoliciesFragment | undefined
  hasDevTimeTrackingWorkableDaysDefinition: boolean
  hasTimeTrackingAnnualisationDailyValue: boolean
  contractVersionPayrollPolicyId?: number
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
  hasPayrollBundle: boolean
  customFieldsContextlessPolicies: ContractsCustomFieldsPoliciesFragment | undefined
}

const UpdateFlowContent = ({
  customFields,
  customValues,
  contractVersion,
  contractVersions,
  employee,
  contractContextlessPolicies,
  jobCatalogContextlessPolicies,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasTimeTrackingAnnualisationDailyValue,
  contractVersionPayrollPolicyId,
  jobCatalogTrees,
  jobCatalogTreeFeatureEnabled,
  hasPayrollBundle,
  customFieldsContextlessPolicies,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const apolloClient = useApolloClient()
  const { compensations, legalEntityContractData, additionalCompensations, contractVersionPolicy } =
    useContractFlow()
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const canSeeGrossSalary =
    contractVersion?._policies?.seeGrossSalary.can ??
    contractContextlessPolicies.contractSeeGrossSalary.can ??
    false
  const canSeeCompensations =
    contractVersion?._policies?.seeAdditionalCompensation.can ??
    contractContextlessPolicies.contractSeeAdditionalCompensation.can ??
    false
  const canSeePayrollPolicy =
    contractVersion?._policies?.seePayrollPolicy.can ??
    contractContextlessPolicies.contractSeePayroll.can ??
    false
  const canSeeJobTitle =
    contractVersion?._policies?.seeJobTitle.can ??
    contractContextlessPolicies.contractSeeJobTitle.can ??
    false
  const canSeeWorkingHours =
    contractVersion?._policies?.seeWorkingHours.can ??
    contractContextlessPolicies.contractSeeWorkingHours.can ??
    false
  const canSeeContractType =
    contractVersion?._policies?.seeContractType.can ??
    contractContextlessPolicies.contractSeeContractType.can ??
    false
  const canSeeTrialPeriod =
    contractVersion?._policies?.seeTrialPeriod.can ??
    contractContextlessPolicies.contractSeeTrialPeriod.can ??
    false
  const canSeeTrialEndDate =
    contractVersion?._policies?.seeTrialPeriodEndDate.can ??
    contractContextlessPolicies.contractSeeTrialPeriodEndDate.can ??
    false
  const canSeeWorkingDayType =
    contractVersion?._policies?.seeWorkingDayType.can ??
    contractContextlessPolicies.contractSeeWorkingDayType.can ??
    false

  const form = useLazyRef(() =>
    getUpdateFlowForm({
      contractVersion,
      employeeId: employee.id,
      payrollPolicyId: contractVersionPayrollPolicyId,
      customFields,
      customValues,
      contractContextlessPolicies,
    })
  ).current

  const onClose = () => {
    navigate(
      navigation.employees.id.contractVersions.fullPath({
        id: employee.id.toString(),
      })
    )
  }
  const [isCurrentStepCompleted, setIsCurrentStepCompleted] = React.useState(false)

  const flowSteps = [
    new DatesStep(
      <DatesAndDetails
        step={1}
        type="request_update"
        form={form}
        translations={datesStepTranslations()}
        setIsCurrentStepCompleted={setIsCurrentStepCompleted}
        contractVersions={contractVersions}
      />
    ),
    new RoleLevelSalaryStep(
      <RoleLevelSalary
        step={2}
        type="update"
        form={form}
        translations={roleLevelSalaryTranslations()}
        setIsCurrentStepCompleted={setIsCurrentStepCompleted}
        jobCatalogTrees={jobCatalogTrees}
        canSeeJobTitle={canSeeJobTitle}
        jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
        employee={employee}
        canSeeGrossSalary={canSeeGrossSalary}
        canSeeCompensations={canSeeCompensations}
        canSeePayrollPolicy={canSeePayrollPolicy}
        canEditPayrollPolicy={canSeePayrollPolicy}
        jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
        legalEntityId={employee.legalEntity?.id ?? 0}
        hasPayrollBundle={hasPayrollBundle}
        contractVersion={contractVersion ?? undefined}
      />
    ),
  ]

  // Avoid showing a blank step when hours permissions are disabled
  if (canSeeWorkingHours) {
    flowSteps.push(
      new WorkHoursStep(
        <WorkHours
          step={3}
          type="update"
          form={form}
          setIsCurrentStepCompleted={setIsCurrentStepCompleted}
          legalEntityCountry={employee.legalEntity?.country}
          canSeeContractType={canSeeContractType}
          canSeeWorkingHours={canSeeWorkingHours}
          hasContractVersions={contractVersions.length > 1}
          hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
          hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
        />
      )
    )
  }
  flowSteps.push(
    new LegalAndExtrasStep(
      <LegalAndExtras
        step={canSeeWorkingHours ? 4 : 3}
        type="update"
        form={form}
        translations={legalAndExtrasTranslations()}
        setIsCurrentStepCompleted={setIsCurrentStepCompleted}
        canSeeContractType={canSeeContractType}
        canSeeTrialPeriod={canSeeTrialPeriod}
        canSeeTrialEndDate={canSeeTrialEndDate}
        canSeeWorkingDayType={canSeeWorkingDayType}
        customFieldsContextlessPolicies={customFieldsContextlessPolicies}
        hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
        employee={employee}
        contractVersion={contractVersion}
      />
    )
  )
  const updateContractFlow = useLazyRef(() => new ContractFlow(flowSteps)).current

  const showFlashMessage = (type: FlashType, message: string) => {
    app.setFlash({
      type,
      message,
    })
  }

  const { hasCompensations, loading: hasCompensationsLoading } = useHasCompensations()

  const [updateContractsContractVersionFlow] = useMutation(
    UpdateContractsContractVersionFlowDocument,
    {
      extractorFn: (data) => data.contractsMutations.updateContractsContractVersionFlow,
    }
  )

  const onSubmit = async () => {
    setIsSubmitting(true)
    const api = ApiClient()
    const contextPolicyId = contractVersionPolicy?.get('payroll_policy_id')
    form.get('payroll_policy_id').set(contextPolicyId ?? contractVersionPayrollPolicyId)
    const data = form.serialized
    data.additional_compensations = (compensations ?? []).map(
      (compensation) => compensation.attributes
    )
    data.custom_fields = getEditedCustomFields(form, customFields)
    data.employee_id = employee.id

    if (hasCompensations) {
      const handled = handleResult(
        await updateContractsContractVersionFlow({
          refetchQueries: [EmployeeProfilePageDocument, ContractVersionsPageDocument],
          awaitRefetchQueries: true,
          variables: {
            // README: this will be remove once the new compensations module is ready
            // sending empty array to avoid errors when "old" compensations have mandatory fields as missing
            additionalCompensations: [],
            newAdditionalCompensations: (additionalCompensations ?? []).map(
              ({ tmpID: _, ...rest }) => {
                return {
                  amount: rest.amountStrategy?.amount,
                  amountStrategyType: rest.amountStrategyType,
                  contractVersionId: rest.contractVersionId,
                  description: rest.description,
                  firstPaymentOn: rest.firstPaymentOn,
                  payrollConceptId: rest.payrollConceptId,
                  recurrence: rest.recurrence,
                  upperLimit:
                    rest.amountStrategy &&
                    isGraphQLType(rest.amountStrategy, 'ContractsVariableAmountStrategy')
                      ? rest.amountStrategy?.upperLimit
                      : 0,
                }
              }
            ),
            customFields: data.custom_fields,
            deActivityType: data.de_activity_type,
            deContractTypeId: data.de_contract_type_id,
            deEmploymentType: data.de_employment_type,
            deFlatRateTax: data.de_flat_rate_tax,
            dePersonalKeyGroupId: data.de_personal_key_group_id,
            effectiveOn: data.effective_on,
            employeeId: employee.id,
            endsOn: data.ends_on,
            esA3innuvaJobPositionPayrollIntegrationCode:
              data.es_a3innuva_job_position_payroll_integration_code,
            esA3innuvaJobPositionPayrollIntegrationName:
              data.es_a3innuva_job_position_payroll_integration_name,
            esAgreementCodePayrollIntegrationCode: data.es_agreement_code_payroll_integration_code,
            esAgreementCodePayrollIntegrationName: data.es_agreement_code_payroll_integration_name,
            esClassificationPayrollIntegrationCode: data.es_classification_payroll_integration_code,
            esClassificationPayrollIntegrationName: data.es_classification_payroll_integration_name,
            esCnoOccupationPayrollIntegrationCode: data.es_cno_occupation_payroll_integration_code,
            esCnoOccupationPayrollIntegrationName: data.es_cno_occupation_payroll_integration_name,
            esContractObservations: data.es_contract_observations,
            esContractTypeId: data.es_contract_type_id,
            esContractTypePayrollIntegrationCode: data.es_contract_type_payroll_integration_code,
            esContractTypePayrollIntegrationName: data.es_contract_type_payroll_integration_name,
            esContributionTypePayrollIntegrationCode:
              data.es_contribution_type_payroll_integration_code,
            esContributionTypePayrollIntegrationName:
              data.es_contribution_type_payroll_integration_name,
            esCotizationGroup: data.es_cotization_group,
            esEducationLevelId: data.es_education_level_id,
            esEducationLevelPayrollIntegrationCode:
              data.es_education_level_payroll_integration_code,
            esEducationLevelPayrollIntegrationName:
              data.es_education_level_payroll_integration_name,
            esHasTeleworkingContract: data.es_has_teleworking_contract,
            esJobDescription: data.es_job_description,
            esOccupationCodePayrollIntegrationCode:
              data.es_occupation_code_payroll_integration_code,
            esOccupationCodePayrollIntegrationName:
              data.es_occupation_code_payroll_integration_name,
            esProfessionalCategoryId: data.es_professional_category_id,
            esRegimePayrollIntegrationCode: data.es_regime_payroll_integration_code,
            esRegimePayrollIntegrationName: data.es_regime_payroll_integration_name,
            esTariffGroupId: data.es_tariff_group_id ? String(data.es_tariff_group_id) : null,
            esTariffGroupName: data.es_tariff_group_name,
            esWorkingDayTypeId: data.es_working_day_type_id,
            frCoefficient: data.fr_coefficient,
            frContractTypeId: data.fr_contract_type_id,
            frEmployeeType: data.fr_employee_type,
            frForfaitJours: data.fr_forfait_jours,
            frJoursParAn: data.fr_jours_par_an,
            frJoursParAnCents: data.fr_jours_par_an_cents,
            frLevelId: data.fr_level_id,
            frMutualId: data.fr_mutual_id,
            frProfessionalCategoryId: data.fr_professional_category_id,
            frStepId: data.fr_step_id,
            frWorkTypeId: data.fr_work_type_id,
            hasTrialPeriod: data.has_trial_period,
            jobCatalogLevelId: data.job_catalog_level_id,
            jobCatalogRoleId: data.job_catalog_role_id,
            jobCatalogTreeNodeUuid: data.job_catalog_tree_node_uuid,
            jobTitle: data.job_title,
            maxLegalYearlyHours: data.max_legal_yearly_hours,
            maximumWeeklyHours: data.maximum_weekly_hours,
            payrollPolicyId: data.payroll_policy_id,
            ptContractTypeId: data.pt_contract_type_id,
            salaryAmount: data.salary_amount,
            salaryFrequency: data.salary_frequency,
            startsOn: data.starts_on,
            trialPeriodEndsOn: data.trial_period_ends_on,
            workingHours: data.working_hours,
            workingHoursFrequency: data.working_hours_frequency,
            workingWeekDays: data.working_week_days,
          },
        }),
        (errors) => {
          form.setErrors(errors)
          showFlashMessage(
            'wrong',
            Object.values(errors).join(', ') ?? i18n.t('contract_versions.flows.errors.unknown')
          )
          setIsSubmitting(false)
        }
      )

      if (handled.success) {
        const contractVersionId =
          handled.data?.contractVersionFlow?.contractVersionId &&
          Number(handled.data?.contractVersionFlow?.contractVersionId)
        showFlashMessage(
          'correct',
          i18n.t('contract_versions.flows.update.request_confirmation', {
            employeeFullName: employee.fullName ?? '',
          })
        )
        contractVersionId &&
          navigate(
            navigation.employees.id.contractVersions.fullPath({
              id: employee.id.toString(),
            })
          )
      }

      return handled
    } else {
      api
        .post(`/contracts/flows/update`, data)
        .then(() => {
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.update.request_confirmation', {
              employeeFullName: employee.fullName ?? '',
            })
          )
          apolloClient.refetchQueries({
            include: [
              EmployeeProfilePageDocument,
              ContractVersionsPageDocument,
              JobPositionLevelDocument,
              JobPositionRoleDocument,
            ],
          })
          navigate(
            navigation.employees.id.contractVersions.fullPath({
              id: employee.id.toString(),
            })
          )
        })
        .catch((e) => {
          if (e instanceof ValidationError) showFlashMessage('wrong', e.message)
          else showFlashMessage('wrong', i18n.t('contract_versions.flows.errors.unknown'))
        })
    }
  }

  if (hasCompensationsLoading) return null

  return (
    <FlowPageContent
      translations={templateTranslations()}
      form={form}
      contractVersion={contractVersion}
      legalEntityContractData={legalEntityContractData}
      title={i18n.t('contract_versions.flows.update.header.request_title', {
        employeeFullName: employee.fullName ?? '',
      })}
      flow={updateContractFlow}
      footerContent={
        <Footer flow={updateContractFlow} employeeFullName={employee.fullName ?? ''} />
      }
      isCurrentStepCompleted={isCurrentStepCompleted}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={() => onSubmit()}
      employee={employee}
    />
  )
}

export default observer(UpdateFlowContent)
